import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Mail, Phone, MapPin } from 'lucide-react';
import { projectsAPI, equipmentAPI, crewAPI, crewAvailabilityAPI, customersAPI, invoicesAPI, emailAPI, companySettingsAPI } from '../services/api';
import { generateQuotePDF } from '../utils/pdfGenerator';
import { useToast } from '../hooks/use-toast';
import { EquipmentSelector, CrewSelector, PriceSummary, AvailabilityWarningBanner } from './project';

const AddProjectDialog = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [availableCrew, setAvailableCrew] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [availabilityWarnings, setAvailabilityWarnings] = useState({});
  const [crewAvailabilityWarnings, setCrewAvailabilityWarnings] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [checkingCrewAvailability, setCheckingCrewAvailability] = useState(false);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    customer: '',
    status: 'quote',
    startDate: '',
    endDate: '',
    location: '',
    totalValue: '',
    equipmentCount: '0',
    crewCount: '0',
    priority: 'medium',
    equipment: [],
    crew: [],
    quoteDate: new Date().toISOString().split('T')[0],
    customQuoteNumber: '',
    autoInvoiceEnabled: false,
    autoInvoiceTime: '08:00'
  });

  useEffect(() => {
    if (open) {
      fetchEquipment();
      fetchCrew();
      fetchCustomers();
    }
  }, [open]);

  // Check availability when dates change
  useEffect(() => {
    if (formData.startDate && formData.endDate && selectedEquipment.length > 0) {
      checkAllEquipmentAvailability();
    }
    if (formData.startDate && formData.endDate && selectedCrew.length > 0) {
      checkAllCrewAvailability();
    }
  }, [formData.startDate, formData.endDate]);

  // Auto-calculate total value when equipment/crew changes
  useEffect(() => {
    const totalEquipmentValue = selectedEquipment.reduce((sum, item) => {
      const equipment = availableEquipment.find(eq => eq.id === item.id);
      return sum + ((equipment?.price || 0) * item.quantity);
    }, 0);
    
    const totalCrewValue = selectedCrew.reduce((sum, crewId) => {
      const crew = availableCrew.find(c => c.id === crewId);
      return sum + (crew?.ratePerDay || 0);
    }, 0);

    const totalDailyRate = totalEquipmentValue + totalCrewValue;
    
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
      setFormData(prev => ({ ...prev, totalValue: (totalDailyRate * days).toFixed(2) }));
    } else {
      setFormData(prev => ({ ...prev, totalValue: totalDailyRate.toFixed(2) }));
    }
  }, [selectedEquipment, selectedCrew, availableEquipment, availableCrew, formData.startDate, formData.endDate]);

  const fetchEquipment = async () => {
    try {
      const res = await equipmentAPI.getAll();
      setAvailableEquipment(res.data.sort((a, b) => (a.serialNumber || '').localeCompare(b.serialNumber || '')));
    } catch (error) {
      console.error('Error fetching equipment:', error);
    }
  };

  const fetchCrew = async () => {
    try {
      const res = await crewAPI.getAll();
      setAvailableCrew(res.data);
    } catch (error) {
      console.error('Error fetching crew:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await customersAPI.getAll();
      setAvailableCustomers(res.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = availableCustomers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customer: customer.name,
        location: customer.address || prev.location
      }));
    }
  };

  const checkEquipmentAvailability = async (equipmentId, quantity) => {
    if (!formData.startDate || !formData.endDate) return null;
    
    try {
      const response = await equipmentAPI.checkAvailability({
        equipment_id: equipmentId,
        start_date: formData.startDate,
        end_date: formData.endDate,
        quantity_needed: quantity
      });
      return response.data;
    } catch (error) {
      console.error('Error checking availability:', error);
      return null;
    }
  };

  const checkCrewAvailability = async (crewId) => {
    if (!formData.startDate || !formData.endDate) return null;
    
    try {
      const response = await crewAvailabilityAPI.checkAvailability({
        crew_id: crewId,
        start_date: formData.startDate,
        end_date: formData.endDate
      });
      return response.data;
    } catch (error) {
      console.error('Error checking crew availability:', error);
      return null;
    }
  };

  const checkAllEquipmentAvailability = async () => {
    if (!formData.startDate || !formData.endDate || selectedEquipment.length === 0) return;
    
    setCheckingAvailability(true);
    const warnings = {};
    
    for (const item of selectedEquipment) {
      const result = await checkEquipmentAvailability(item.id, item.quantity);
      if (result) {
        warnings[item.id] = result;
      }
    }
    
    setAvailabilityWarnings(warnings);
    setCheckingAvailability(false);
  };

  const checkAllCrewAvailability = async () => {
    if (!formData.startDate || !formData.endDate || selectedCrew.length === 0) return;
    
    setCheckingCrewAvailability(true);
    const warnings = {};
    
    for (const crewId of selectedCrew) {
      const result = await checkCrewAvailability(crewId);
      if (result) {
        warnings[crewId] = result;
      }
    }
    
    setCrewAvailabilityWarnings(warnings);
    setCheckingCrewAvailability(false);
  };

  const handleAddEquipment = async (equipmentId) => {
    const existingIndex = selectedEquipment.findIndex(item => item.id === equipmentId);
    if (existingIndex === -1) {
      const equipment = availableEquipment.find(eq => eq.id === equipmentId);
      const maxQty = equipment?.available || 1;
      const newSelected = [...selectedEquipment, { id: equipmentId, quantity: 1, maxQuantity: maxQty }];
      setSelectedEquipment(newSelected);
      updateEquipmentFormData(newSelected);
      
      // Check availability for this equipment
      if (formData.startDate && formData.endDate) {
        const result = await checkEquipmentAvailability(equipmentId, 1);
        if (result) {
          setAvailabilityWarnings(prev => ({ ...prev, [equipmentId]: result }));
        }
      }
    }
  };

  const handleRemoveEquipment = (equipmentId) => {
    const newSelected = selectedEquipment.filter(item => item.id !== equipmentId);
    setSelectedEquipment(newSelected);
    updateEquipmentFormData(newSelected);
    setAvailabilityWarnings(prev => {
      const updated = { ...prev };
      delete updated[equipmentId];
      return updated;
    });
  };

  const handleQuantityChange = async (equipmentId, change) => {
    const newSelected = selectedEquipment.map(item => {
      if (item.id === equipmentId) {
        const newQty = Math.max(1, Math.min(item.maxQuantity, item.quantity + change));
        return { ...item, quantity: newQty };
      }
      return item;
    });
    setSelectedEquipment(newSelected);
    updateEquipmentFormData(newSelected);
    
    // Recheck availability with new quantity
    const item = newSelected.find(i => i.id === equipmentId);
    if (item && formData.startDate && formData.endDate) {
      const result = await checkEquipmentAvailability(equipmentId, item.quantity);
      if (result) {
        setAvailabilityWarnings(prev => ({ ...prev, [equipmentId]: result }));
      }
    }
  };

  const updateEquipmentFormData = (equipment) => {
    const totalItems = equipment.reduce((sum, item) => sum + item.quantity, 0);
    setFormData(prev => ({ 
      ...prev, 
      equipment: equipment.map(e => ({ id: e.id, quantity: e.quantity })),
      equipmentCount: totalItems.toString()
    }));
  };

  const handleAddCrew = async (crewId) => {
    if (!selectedCrew.includes(crewId)) {
      const newSelected = [...selectedCrew, crewId];
      setSelectedCrew(newSelected);
      setFormData(prev => ({ ...prev, crew: newSelected, crewCount: newSelected.length.toString() }));
      
      // Check availability for this crew member
      if (formData.startDate && formData.endDate) {
        const result = await checkCrewAvailability(crewId);
        if (result) {
          setCrewAvailabilityWarnings(prev => ({ ...prev, [crewId]: result }));
        }
      }
    }
  };

  const handleRemoveCrew = (crewId) => {
    const newSelected = selectedCrew.filter(id => id !== crewId);
    setSelectedCrew(newSelected);
    setFormData(prev => ({ ...prev, crew: newSelected, crewCount: newSelected.length.toString() }));
    setCrewAvailabilityWarnings(prev => {
      const updated = { ...prev };
      delete updated[crewId];
      return updated;
    });
  };

  const hasEquipmentAvailabilityIssues = Object.values(availabilityWarnings).some(w => !w.is_available);
  const hasCrewAvailabilityIssues = Object.values(crewAvailabilityWarnings).some(w => !w.is_available);
  const hasAvailabilityIssues = hasEquipmentAvailabilityIssues || hasCrewAvailabilityIssues;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasAvailabilityIssues) {
      const proceed = window.confirm('Sommige apparatuur of personeelsleden hebben beschikbaarheidsconflicten. Wilt u toch doorgaan?');
      if (!proceed) return;
    }
    setLoading(true);
    try {
      const totalItems = selectedEquipment.reduce((sum, item) => sum + item.quantity, 0);
      const projectResponse = await projectsAPI.create({
        ...formData,
        totalValue: parseFloat(formData.totalValue) || 0,
        equipmentCount: totalItems,
        crewCount: selectedCrew.length,
        equipment: selectedEquipment.map(e => ({ id: e.id, quantity: e.quantity })),
        crew: selectedCrew
      });
      
      // Auto-send email with PDF
      try {
        await new Promise(resolve => setTimeout(resolve, 2500));
        const invoicesRes = await invoicesAPI.getAll({});
        // Sort invoices by date (newest first) and find the one for this project
        const sortedInvoices = (invoicesRes.data || []).sort((a, b) => new Date(b.issueDate || 0) - new Date(a.issueDate || 0));
        const projectInvoice = sortedInvoices.find(inv => inv.projectId === projectResponse.data?.id);
        
        if (projectInvoice && selectedCustomer?.email) {
          let company = {}, logo = null;
          try {
            const [settingsRes, logoRes] = await Promise.all([companySettingsAPI.get(), companySettingsAPI.getLogo()]);
            company = settingsRes.data || {};
            logo = logoRes.data?.logo || null;
          } catch (e) {}

          const days = formData.startDate && formData.endDate ? Math.max(1, Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24)) + 1) : 1;
          const items = selectedEquipment.map(sel => {
            const eq = availableEquipment.find(e => e.id === sel.id);
            return { name: eq?.name || 'Item', category: eq?.category || 'APPARATUUR', quantity: sel.quantity, pricePerDay: eq?.price || 0 };
          });
          const crewItems = selectedCrew.map(c => {
            const cr = availableCrew.find(crew => crew.id === c.id);
            return { name: cr?.name || 'Medewerker', role: cr?.role || '', ratePerDay: cr?.dayRate || 0 };
          });
          const totalExclBtw = projectInvoice.amount || parseFloat(formData.totalValue) || 0;
          
          const pdfData = {
            quoteNumber: projectInvoice.invoiceNumber, quoteDate: projectInvoice.issueDate || new Date().toISOString(),
            customer: formData.customer, customerContact: selectedCustomer?.name || formData.customer,
            customerEmail: selectedCustomer?.email || '', customerPhone: selectedCustomer?.phone || '',
            customerAddress: selectedCustomer?.address || '', productionDate: formData.startDate,
            productionEndDate: formData.endDate, location: formData.location || '', items, crew: crewItems, days,
            subtotal: totalExclBtw, staffelDiscount: 0, staffelDetails: [], discountAmount: 0,
            totalExclBtw, btwAmount: totalExclBtw * 0.21, totalInclBtw: totalExclBtw * 1.21, logo, company
          };

          const doc = generateQuotePDF(pdfData);
          const pdfBase64 = doc.output('datauristring').split(',')[1];

          await emailAPI.sendInvoice({
            recipient_email: selectedCustomer.email, invoice_number: projectInvoice.invoiceNumber,
            invoice_id: projectInvoice.id, customer_name: formData.customer, project_name: formData.name,
            amount: totalExclBtw, due_date: new Date(Date.now() + 14*24*60*60*1000).toLocaleDateString('nl-NL'),
            issue_date: new Date(projectInvoice.issueDate).toLocaleDateString('nl-NL'), pdf_base64: pdfBase64,
            pdf_filename: projectInvoice.invoiceNumber + '.pdf'
          });
          toast({ title: "Gelukt", description: "Project aangemaakt en offerte verstuurd naar " + selectedCustomer.email });
        } else {
          toast({ title: "Gelukt", description: "Project succesvol aangemaakt" });
        }
      } catch (emailError) {
        console.error('Auto-email error:', emailError);
        toast({ title: "Gelukt", description: "Project aangemaakt (email kon niet verstuurd worden)" });
      }
      
      setOpen(false);
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({ title: "Fout", description: "Kan project niet aanmaken", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      customer: '',
      status: 'quote',
      startDate: '',
      endDate: '',
      location: '',
      totalValue: '',
      equipmentCount: '0',
      crewCount: '0',
      priority: 'medium',
      equipment: [],
      crew: [],
      quoteDate: new Date().toISOString().split('T')[0],
      customQuoteNumber: '',
      autoInvoiceEnabled: false,
      autoInvoiceTime: '08:00'
    });
    setSelectedEquipment([]);
    setSelectedCrew([]);
    setSelectedCustomer(null);
    setAvailabilityWarnings({});
    setCrewAvailabilityWarnings({});
  };

  // Calculate totals for PriceSummary
  const equipmentDailyTotal = selectedEquipment.reduce((sum, item) => {
    const eq = availableEquipment.find(e => e.id === item.id);
    return sum + ((eq?.price || 0) * item.quantity);
  }, 0);

  const crewDailyTotal = selectedCrew.reduce((sum, crewId) => {
    const crew = availableCrew.find(c => c.id === crewId);
    return sum + (crew?.ratePerDay || 0);
  }, 0);

  const totalEquipmentItems = selectedEquipment.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg">
          <Plus className="w-4 h-4 mr-2" />
          Nieuw Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nieuw Project Aanmaken</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Projectnaam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Voer projectnaam in"
                required
              />
            </div>
            <div>
              <Label htmlFor="customer">Klant *</Label>
              <Select onValueChange={handleCustomerSelect} value={selectedCustomer?.id || ''}>
                <SelectTrigger data-testid="customer-select-trigger">
                  <SelectValue placeholder="Selecteer een klant" />
                </SelectTrigger>
                <SelectContent>
                  {availableCustomers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} ({customer.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer details display */}
            {selectedCustomer && (
              <div className="col-span-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">Klantgegevens</h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <span>{selectedCustomer.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone className="w-4 h-4 text-blue-500" />
                    <span>{selectedCustomer.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    <span>{selectedCustomer.address || 'Geen adres'}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="startDate">Startdatum *</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="endDate">Einddatum *</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="location">Locatie *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Voer locatie in"
                required
              />
            </div>
            <div>
              <Label htmlFor="quoteDate">Offerte Datum</Label>
              <Input
                id="quoteDate"
                type="date"
                value={formData.quoteDate}
                onChange={(e) => setFormData({ ...formData, quoteDate: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="customQuoteNumber">Offerte Nummer (optioneel)</Label>
              <Input
                id="customQuoteNumber"
                value={formData.customQuoteNumber}
                onChange={(e) => setFormData({ ...formData, customQuoteNumber: e.target.value })}
                placeholder="OFF-2026-XXXX (leeg = automatisch)"
              />
            </div>
            <div className="col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-base font-semibold">Automatische Facturatie</Label>
                  <p className="text-sm text-gray-600">Verstuur factuur automatisch na afloop huurperiode</p>
                </div>
                <input
                  type="checkbox"
                  id="autoInvoiceEnabled"
                  checked={formData.autoInvoiceEnabled}
                  onChange={(e) => setFormData({ ...formData, autoInvoiceEnabled: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300"
                />
              </div>
              {formData.autoInvoiceEnabled && (
                <div className="mt-3">
                  <Label htmlFor="autoInvoiceTime">Verzendtijd</Label>
                  <Input
                    id="autoInvoiceTime"
                    type="time"
                    value={formData.autoInvoiceTime}
                    onChange={(e) => setFormData({ ...formData, autoInvoiceTime: e.target.value })}
                    className="w-32"
                  />
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="totalValue">Totale Waarde (€) <span className="text-xs text-green-600">(Automatisch berekend)</span></Label>
              <Input
                id="totalValue"
                type="number"
                step="0.01"
                value={formData.totalValue}
                onChange={(e) => setFormData({ ...formData, totalValue: e.target.value })}
                className="bg-green-50 font-semibold"
              />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote">Offerte</SelectItem>
                  <SelectItem value="pending">In Afwachting</SelectItem>
                  <SelectItem value="confirmed">Bevestigd</SelectItem>
                  <SelectItem value="in-progress">In Uitvoering</SelectItem>
                  <SelectItem value="completed">Afgerond</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="priority">Prioriteit</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Laag</SelectItem>
                  <SelectItem value="medium">Gemiddeld</SelectItem>
                  <SelectItem value="high">Hoog</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <EquipmentSelector
            availableEquipment={availableEquipment}
            selectedEquipment={selectedEquipment}
            availabilityWarnings={availabilityWarnings}
            checkingAvailability={checkingAvailability}
            hasDates={!!(formData.startDate && formData.endDate)}
            onAddEquipment={handleAddEquipment}
            onRemoveEquipment={handleRemoveEquipment}
            onQuantityChange={handleQuantityChange}
          />

          <CrewSelector
            availableCrew={availableCrew}
            selectedCrew={selectedCrew}
            availabilityWarnings={crewAvailabilityWarnings}
            checkingAvailability={checkingCrewAvailability}
            hasDates={!!(formData.startDate && formData.endDate)}
            onAddCrew={handleAddCrew}
            onRemoveCrew={handleRemoveCrew}
          />

          <PriceSummary
            equipmentDailyTotal={equipmentDailyTotal}
            crewDailyTotal={crewDailyTotal}
            totalEquipmentItems={totalEquipmentItems}
            crewCount={selectedCrew.length}
            startDate={formData.startDate}
            endDate={formData.endDate}
            totalValue={formData.totalValue}
          />

          <AvailabilityWarningBanner hasIssues={hasAvailabilityIssues} />
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
              Annuleren
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className={`${hasAvailabilityIssues ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gradient-to-r from-purple-600 to-blue-600'}`}
            >
              {loading ? 'Bezig...' : hasAvailabilityIssues ? 'Toch Aanmaken' : 'Project Aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddProjectDialog;
